import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Activity, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

import { API } from "../config/api";
import { enrichWithAnchoredVwap } from "../utils/vwap";

const MovingAverages = ({ ticker }) => {
  const [maData, setMaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showChart, setShowChart] = useState(true);

  useEffect(() => {
    if (ticker) {
      fetchMovingAverages();
    }
  }, [ticker]);

  const fetchMovingAverages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/moving-averages/${ticker}`);
      setMaData(response.data);
    } catch (error) {
      console.error("Error fetching moving averages:", error);
      console.warn("Failed to load moving averages - retrying silently in background");
    } finally {
      setLoading(false);
    }
  };

  const getTrendColor = (signal) => {
    if (signal === 'strong_bullish') return 'bg-green-600 text-white';
    if (signal === 'bullish') return 'bg-green-500 text-white';
    if (signal === 'bearish') return 'bg-red-500 text-white';
    if (signal === 'strong_bearish') return 'bg-red-600 text-white';
    return 'bg-slate-400 text-white';
  };

  const getPositionColor = (position) => {
    return position === 'above' ? 'text-green-600' : 'text-red-600';
  };

  // Compute per-bar Anchored VWAP for the chart period (200d daily)
  const enrichedChartData = useMemo(
    () => (maData?.chart_data ? enrichWithAnchoredVwap(maData.chart_data) : []),
    [maData]
  );

  return (
    <Card className="border-slate-200 bg-white/80 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-600" />
          Moving Averages Analysis
        </CardTitle>
        <p className="text-xs text-slate-600">
          SMA & EMA for 5, 10, 20, 50, 100, and 200 trading days
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            <span className="ml-2 text-sm text-slate-600">Calculating moving averages...</span>
          </div>
        ) : maData ? (
          <div className="space-y-6">
            {/* Trend Signals */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700">Overall Trend:</span>
                <Badge className={getTrendColor(maData.signals.trend_signal)}>
                  {maData.signals.overall_trend}
                </Badge>
              </div>
              
              {maData.signals.golden_cross && (
                <Badge className="bg-yellow-500 text-white flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Golden Cross Detected!
                </Badge>
              )}
              
              {maData.signals.death_cross && (
                <Badge className="bg-red-700 text-white flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" />
                  Death Cross Detected!
                </Badge>
              )}
              
              <div className="text-sm text-slate-700">
                Current Price: <span className="font-bold text-slate-900">${maData.current_price}</span>
              </div>
            </div>

            {/* Toggle Chart */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowChart(!showChart)}
                className="text-xs text-purple-600 hover:text-purple-700 underline"
              >
                {showChart ? 'Hide Chart' : 'Show Chart'}
              </button>
            </div>

            {/* Chart */}
            {showChart && (
              <div>
                <h4 className="text-sm font-semibold text-slate-900 mb-3">200-Day Price & Moving Averages Chart</h4>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={enrichedChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                      interval={Math.floor(enrichedChartData.length / 10)}
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }}
                      domain={['auto', 'auto']}
                      label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    
                    <Line type="monotone" dataKey="price" stroke="#1e293b" strokeWidth={2.5} name="Price" dot={false} />
                    <Line type="monotone" dataKey="sma_5" stroke="#06b6d4" strokeWidth={1.5} name="SMA-5" dot={false} strokeDasharray="5 5" />
                    <Line type="monotone" dataKey="sma_20" stroke="#3b82f6" strokeWidth={2} name="SMA-20" dot={false} />
                    <Line type="monotone" dataKey="sma_50" stroke="#8b5cf6" strokeWidth={2} name="SMA-50" dot={false} />
                    <Line type="monotone" dataKey="sma_200" stroke="#ef4444" strokeWidth={2.5} name="SMA-200" dot={false} />
                    <Line type="monotone" dataKey="vwap" stroke="#ee82ee" strokeWidth={2.5} strokeDasharray="4 3" name="VWAP (anchored)" dot={false} connectNulls isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* SMA Table */}
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Simple Moving Averages (SMA)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b-2 border-slate-300">
                      <th className="text-left p-2 font-semibold text-slate-700">Period</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Value</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Position</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Distance</th>
                      <th className="text-center p-2 font-semibold text-slate-700">Signal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[5, 10, 20, 50, 100, 200].map(period => {
                      const sma = maData.smas[period];
                      if (!sma) return null;
                      return (
                        <tr key={`sma-${period}`} className="border-b border-slate-200 hover:bg-slate-50">
                          <td className="p-2 font-medium">SMA-{period}</td>
                          <td className="p-2 text-right font-mono">${sma.value.toFixed?.(2) || "0.00"}</td>
                          <td className={`p-2 text-right font-semibold ${getPositionColor(sma.position)}`}>
                            {sma.position.toUpperCase()}
                          </td>
                          <td className="p-2 text-right font-mono">{sma.distance_pct.toFixed?.(2) || "0.00"}%</td>
                          <td className="p-2 text-center">
                            <Badge variant="outline" className={sma.signal === 'bullish' ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'}>
                              {sma.signal}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* EMA Table */}
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Exponential Moving Averages (EMA)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b-2 border-slate-300">
                      <th className="text-left p-2 font-semibold text-slate-700">Period</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Value</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Position</th>
                      <th className="text-right p-2 font-semibold text-slate-700">Distance</th>
                      <th className="text-center p-2 font-semibold text-slate-700">Signal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[5, 10, 20, 50, 100, 200].map(period => {
                      const ema = maData.emas[period];
                      if (!ema) return null;
                      return (
                        <tr key={`ema-${period}`} className="border-b border-slate-200 hover:bg-slate-50">
                          <td className="p-2 font-medium">EMA-{period}</td>
                          <td className="p-2 text-right font-mono">${ema.value.toFixed?.(2) || "0.00"}</td>
                          <td className={`p-2 text-right font-semibold ${getPositionColor(ema.position)}`}>
                            {ema.position.toUpperCase()}
                          </td>
                          <td className="p-2 text-right font-mono">{ema.distance_pct.toFixed?.(2) || "0.00"}%</td>
                          <td className="p-2 text-center">
                            <Badge variant="outline" className={ema.signal === 'bullish' ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'}>
                              {ema.signal}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Interpretation */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h5 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-purple-600" />
                How to Interpret
              </h5>
              <div className="text-xs text-slate-700 space-y-1">
                <p><strong>Position:</strong> Price above MA = Bullish; Price below MA = Bearish</p>
                <p><strong>Golden Cross:</strong> 50-day crosses above 200-day = Strong bullish signal</p>
                <p><strong>Death Cross:</strong> 50-day crosses below 200-day = Strong bearish signal</p>
                <p><strong>SMA vs EMA:</strong> EMA reacts faster to price changes; SMA is smoother</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-600 py-8">No moving averages data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default MovingAverages;
